import React, { forwardRef, useEffect, useRef, useState } from "react";
import axios from "axios";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { global, globalMenu } from "@shared/libs/Global";
import { dateToString } from "@shared/utils/DateUtils";
import { showDialog } from "@widgets/CommonDialog";
import SnButton from "@shared/components/SnButton";
import SnGrid from "@shared/components/GridV2/SnGrid";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

const SN_PRD_BAS_REG_001_CHEM_POP_002 = forwardRef(
  ({ selectedData }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();

    const gridRef = useRef<any>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [columns, setColums] = useState<any[]>([]);
    const [dataSource, setDataSource] = useState<any[]>([]);

    const cdMenu = globalMenu.getState().activeMenuInfo.cd_menu || "";
    const cdModule = globalMenu.getState().activeMenuInfo.cd_module || "";
    const noDocu = selectedData?.cd_group || "";

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_PRD_BAS_REG_001_CHEM_POP_002",
            {}
          );
          setColums(rtn.grid.grid1);

          setTimeout(() => {
            onSearch();
          }, 100);
        } catch (error) {}
      };
      fetchform();
    }, []);

    const onSearch = async () => {
      try {
        if (!noDocu) {
          setDataSource([]);
          return;
        }

        const params = {
          id: "com_attachfile_01",
          params: {
            cd_firm: userInfo.cd_firm,
            cd_menu: cdMenu,
            no_docu: noDocu,
            no_line_docu: 0,
          },
        };
        const result: any = await fnGetDataSet(params);
        setDataSource(result[0] || []);
      } catch (error: any) {
        showDialog("error", error.message);
      }
    };

    const onUploadClick = () => {
      fileInputRef.current?.click();
    };

    const handleFileChange = async (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const fileList = event.target.files;
      if (!fileList || fileList.length === 0) return;

      try {
        const formData = new FormData();
        for (let i = 0; i < fileList.length; i++) {
          formData.append("files", fileList[i]);
        }
        formData.append("cdFirm", userInfo.cd_firm || "");
        formData.append("cdMenu", cdMenu);
        formData.append("noDocu", noDocu);
        formData.append("noLineDocu", "0");
        formData.append("dtDocu", dateToString(new Date()));
        formData.append("cdUser", userInfo.cd_user || "");
        formData.append("cdModule", cdModule);

        const res = await axios.post("/snotes/file/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
          },
        });

        if (res.data.result) {
          const if_status = res.data.result.if_status;
          if (if_status === "E") {
            throw new Error(res.data.result.message);
          }
        }

        await showDialog("success", "파일이 저장되었습니다.");
        await onSearch();
      } catch (err) {
        console.error("업로드 실패:", err);
        await showDialog("error", "파일 업로드를 실패했습니다.");
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };

    const onDownload = async () => {
      const focusedRowNode = gridRef.current?.focusedRowNode();
      if (!focusedRowNode) {
        await showDialog("alter", "파일을 선택하세요.");
        return;
      }

      try {
        const file = focusedRowNode.data;
        const res = await axios.post(
          "/snotes/file/download",
          { id: file.id, cdModule: cdModule },
          {
            headers: {
              "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
            },
            responseType: "blob",
          }
        );

        if (res.data.result) {
          const if_status = res.data.result.if_status;
          if (if_status === "E") {
            throw new Error(res.data.result.message);
          }
        }

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file.nm_file);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err) {
        console.error("파일 다운로드를 실패했습니다.");
        await showDialog("error", "파일 다운로드를 실패했습니다.");
      }
    };

    const onDelete = async () => {
      const focusedRowNode = gridRef.current?.focusedRowNode();
      if (!focusedRowNode) {
        await showDialog("alter", "파일을 선택하세요.");
        return;
      }

      try {
        const confirm = await showDialog("confirm", "파일을 삭제하시겠습니까?");
        if (!confirm) return;

        const file = focusedRowNode.data;
        const res = await axios.delete(
          `/snotes/file/delete/${cdModule}/${file.id}`,
          {
            withCredentials: true,
            headers: {
              "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
            },
          }
        );

        if (res.data.result) {
          const if_status = res.data.result.if_status;
          if (if_status === "E") {
            throw new Error(res.data.result.message);
          }
        }

        await showDialog("success", "파일이 삭제되었습니다.");
        await onSearch();
      } catch (err) {
        console.error("삭제 실패:", err);
        await showDialog("error", "파일 삭제를 실패했습니다.");
      }
    };

    return (
      <div className="h-full flex flex-col">
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "4px",
            marginBottom: "6px",
          }}
        >
          <SnButton text="조회" onClick={onSearch} />
          <SnButton text="등록" onClick={onUploadClick} />
          <SnButton text="다운로드" onClick={onDownload} />
          <SnButton text="삭제" onClick={onDelete} />
        </div>
        <div className="flex-1 min-h-0">
          <SnGrid
            ref={gridRef}
            columnConfig={columns}
            rowData={dataSource}
            etcProps={{ rowSelection: "single" }}
          />
        </div>
        <input
          type="file"
          multiple
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>
    );
  }
);

export default SN_PRD_BAS_REG_001_CHEM_POP_002;
